import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable, Injector, untracked } from '@angular/core';
import { concatMap, filter, map, switchMap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { Store } from '@ngrx/store';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { selectMainDate } from '@app/main/main-screen/store/main-selection/main-date.selectors';
import { FloatingPopupTabsService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-tabs.service';
import { ILoggerDeployment } from '@app/api/interfaces/logger-deployment-data.interface';
import {
  selectGlobalMainDataIsLoaded,
  selectGlobalMainDataIsLoading,
} from '@app/main/main-screen/store/main-data/main-data.selectors';
import {
  IFpLoggerNoiseManagerState,
  initialFpLoggerNoiseManagerState,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/services/fp-logger-noise-manager/fp-logger-noise-manager.state';
import { FP_LOGGER_NOISE_TAB_CONNECT_SERVICES } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/providers/fp-logger-noise-tab-connect-services.token';
import {
  FpLoggerNoiseManagerActionChangeTab,
  FpLoggerNoiseManagerActionGetSelectedDataUpdated,
  FpLoggerNoiseManagerActionGlobalDataLoadingStateChanged,
  FpLoggerNoiseManagerActionInit,
  FpLoggerNoiseManagerActions,
  FpLoggerNoiseManagerActionType,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/services/fp-logger-noise-manager/fp-logger-noise-manager.actions';
import { FpLoggerNoiseTabs } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/enums/fp-logger-noise-tabs.enum';
import { selectAllLoggerMeasurements } from '@app/main/main-screen/store/data/logger-measurements/logger-measurements.selectors';
import { ILoggerMeasurement } from '@app/api/interfaces/logger-measurement.interface';
import { selectAllLoggerDeployments } from '@app/main/main-screen/store/data/logger-deployments/logger-deployments.selectors';
import { DayjsLocaleService } from '@app/core/i18n/services/dayjs-locale.service';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { LdDataAggregationService } from '@app/main/leak-detection/services/leak-detection-data-aggregation/leak-detection-data-aggregation.service';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { getLoggerSerialNoMapToMeasurements } from '@app/main/leak-detection/utils/logger-serial-no-measurements';
import { ITranslationSubscription } from '@app/core/interfaces/localization';

@Injectable()
export class FpLoggerNoiseManagerStore
  extends ComponentStore<IFpLoggerNoiseManagerState>
  implements IGcComponentStore<FpLoggerNoiseManagerActions>
{
  private injector = inject(Injector);
  private store: Store = inject(Store);
  public readonly data = inject<ILdLogger>(FLOATING_POPUP_DATA);
  private readonly tabConnectServices = inject(FP_LOGGER_NOISE_TAB_CONNECT_SERVICES);
  public readonly floatingPopupTabs = inject(FloatingPopupTabsService);
  private dayjsLocale = inject(DayjsLocaleService);
  private ldDataAggregationService = inject(LdDataAggregationService);
  private floatingPopupService = inject(FloatingPopupService);
  private formattingData = inject(FormattingDataService);

  private mainDate$ = this.store.select(selectMainDate);
  private mainDate = toSignal<string | undefined>(this.mainDate$);

  private allLoggerDeployments$: Observable<ILoggerDeployment[]> = this.store.select(selectAllLoggerDeployments);
  private allLoggerMeasurements$: Observable<ILoggerMeasurement[]> = this.store.select(selectAllLoggerMeasurements);

  private allLoggerDeployments = toSignal(this.allLoggerDeployments$, { initialValue: [] as ILoggerDeployment[] });
  private allLoggerMeasurements = toSignal(this.allLoggerMeasurements$, { initialValue: [] as ILoggerMeasurement[] });

  private globalMainDataIsLoading$ = this.store.select(selectGlobalMainDataIsLoading);
  private globalMainDataIsLoaded$ = this.store.select(selectGlobalMainDataIsLoaded);

  private newestSelectedData$ = this.globalMainDataIsLoaded$.pipe(
    takeUntilDestroyed(),
    switchMap(isGlobalDataLoaded => {
      return combineLatest([this.allLoggerDeployments$, this.allLoggerMeasurements$]).pipe(
        filter(() => isGlobalDataLoaded)
      );
    }),
    map(([allLoggerDeployments, allLoggerMeasurements]) =>
      // TODO check this logic findProperFpLoggerNoiseData after implementation LdDataTimedService(where should be source of truth, from store(all) or LdDataTimedService(filtered))
      this.findProperFpLoggerNoiseData(allLoggerDeployments, allLoggerMeasurements)
    )
  );

  constructor() {
    super(initialFpLoggerNoiseManagerState);

    this.popupId$
      .pipe(
        filter((popupId): popupId is string => !!popupId),
        takeUntilDestroyed(),
        switchMap(popupId =>
          untracked(() => toObservable(this.floatingPopupTabs.getActive(popupId), { injector: this.injector }))
        ),
        filter((activeTab): activeTab is FpLoggerNoiseTabs => !!activeTab)
      )
      .subscribe(activeTab => {
        const activeTabConnectService = this.tabConnectServices.find(s => s.isActive(activeTab)) || null;

        this.dispatch({
          type: FpLoggerNoiseManagerActionType.ChangeTab,
          activeTabConnectService,
        });
      });

    this.newestSelectedData$.pipe(takeUntilDestroyed()).subscribe(selectedData => {
      this.dispatch({
        type: FpLoggerNoiseManagerActionType.SelectedDataUpdated,
        selectedData,
      });
    });

    this.globalMainDataIsLoading$.pipe(takeUntilDestroyed()).subscribe(isLoading => {
      this.dispatch({
        type: FpLoggerNoiseManagerActionType.GlobalDataLoadingStateChanged,
        loading: isLoading,
      });
    });
  }

  public readonly popupId$ = this.select(s => s.popupId, { equal });
  public readonly selectedData$ = this.select(s => s.selectedData, { equal });
  public readonly initialized$ = this.select(s => s.initialized, { equal });

  public readonly activeTabConnectService$ = this.select(s => s.activeTabConnectService, { equal });

  public activeTabLoading$ = this.activeTabConnectService$.pipe(
    filter(tabConnectService => !!tabConnectService),
    switchMap(tabConnectService => tabConnectService?.localLoading())
  );

  public activeTabIsChartFullscreen$ = this.activeTabConnectService$.pipe(
    filter(tabConnectService => !!tabConnectService),
    switchMap(tabConnectService =>
      tabConnectService?.isChartFullscreen ? tabConnectService?.isChartFullscreen() : of(false)
    )
  );

  public readonly loading$ = this.select(s => s.loading, { equal });

  public dispatch = this.effect((action$: Observable<FpLoggerNoiseManagerActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpLoggerNoiseManagerActionType.Init: {
            this.reducerInit(action);
            this.effectInit(action);
            break;
          }
          case FpLoggerNoiseManagerActionType.SelectedDataUpdated: {
            this.reducerSelectedDataUpdated(action);
            this.effectSelectedDataUpdated(action);
            break;
          }
          case FpLoggerNoiseManagerActionType.ChangeTab: {
            this.reducerChangeActiveTabConnectService(action);
            this.effectChangeActiveTabConnectService(action);
            break;
          }
          case FpLoggerNoiseManagerActionType.GlobalDataLoadingStateChanged: {
            this.reducerUpdateLoading(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerInit(action: FpLoggerNoiseManagerActionInit) {
    const state = this.get();

    if (state.initialized) {
      return;
    }

    this.patchState({
      popupId: action.popupId,
      selectedData: action.selectedData,
      initialized: true,
    });
  }

  private reducerSelectedDataUpdated(action: FpLoggerNoiseManagerActionGetSelectedDataUpdated) {
    this.patchState({
      selectedData: action.selectedData,
    });
  }

  private reducerChangeActiveTabConnectService(action: FpLoggerNoiseManagerActionChangeTab) {
    this.patchState({
      activeTabConnectService: action.activeTabConnectService,
    });
  }

  private reducerUpdateLoading(action: FpLoggerNoiseManagerActionGlobalDataLoadingStateChanged) {
    this.patchState({
      loading: action.loading,
    });
  }

  private effectInit(action: FpLoggerNoiseManagerActionInit) {
    this.updateTitleInfo(action.selectedData);
  }

  private effectSelectedDataUpdated(action: FpLoggerNoiseManagerActionGetSelectedDataUpdated) {
    const state = this.get();

    const mainDate = this.mainDate();
    const selectedData = action.selectedData;
    const activeTabConnectService = state.activeTabConnectService;

    if (mainDate && selectedData && activeTabConnectService) {
      activeTabConnectService.updateData(mainDate, selectedData);
    }

    this.updateTitleInfo(action.selectedData);
  }

  private effectChangeActiveTabConnectService(action: FpLoggerNoiseManagerActionChangeTab) {
    const state = this.get();

    const mainDate = this.mainDate();
    const selectedData = state.selectedData;

    if (mainDate && selectedData && action.activeTabConnectService) {
      action.activeTabConnectService.initData(mainDate, selectedData);
    }
  }

  private findProperFpLoggerNoiseData(
    loggerDeployments: ILoggerDeployment[],
    loggerMeasurements: ILoggerMeasurement[]
  ): ILdLogger | null {
    // a lot of deployments
    // one logger can be deployed several times a day
    const loggerMeasurementsIdMap = new Map(loggerMeasurements.map(c => [c.id, c]));

    const loggerMeasurement = this.data.measurement && loggerMeasurementsIdMap.get(this.data.measurement.id);

    // if the same data
    if (loggerMeasurement) {
      const relatedDeployment =
        loggerDeployments.find(ld => ld.id === loggerMeasurement.deploymentId) || this.data.deployment;

      return this.ldDataAggregationService.prepareLdLogger({ ...relatedDeployment }, { ...loggerMeasurement });
    }

    const relatedDeployments = loggerDeployments.filter(ld => ld.serialNo === this.data.deployment.serialNo);

    if (!relatedDeployments.length) {
      return null;
    }

    const sortedRelatedDeployments = relatedDeployments.sort((a, b) => {
      const aDate = this.dayjsLocale.dayjs(a.deployTime);
      const bDate = this.dayjsLocale.dayjs(b.deployTime);
      return bDate.millisecond() - aDate.millisecond();
    });

    let newLoggerDeployment: ILoggerDeployment | null = sortedRelatedDeployments[0] || null;
    let newLoggerMeasurement: ILoggerMeasurement | null = null;

    for (const deployment of sortedRelatedDeployments) {
      const measurement = this.findRelatedMeasurementToDeployment(deployment, loggerMeasurements);

      if (measurement) {
        newLoggerDeployment = deployment;
        newLoggerMeasurement = measurement;
        break;
      }
    }

    if (!newLoggerDeployment) {
      return null;
    }

    return this.ldDataAggregationService.prepareLdLogger(
      { ...newLoggerDeployment },
      newLoggerMeasurement ? { ...newLoggerMeasurement } : undefined
    );
  }

  private findRelatedMeasurementToDeployment(
    deployment: ILoggerDeployment,
    measurements: ILoggerMeasurement[]
  ): ILoggerMeasurement | null {
    const relatedMeasurementToDeployment = measurements
      .filter(lm => lm.deploymentId === deployment.id)
      .sort((a, b) => {
        const aDate = this.dayjsLocale.dayjs(a.measStartTime || '');
        const bDate = this.dayjsLocale.dayjs(b.measStartTime || '');

        return bDate.millisecond() - aDate.millisecond();
      });

    if (!relatedMeasurementToDeployment.length) {
      return null;
    }

    return relatedMeasurementToDeployment[0];
  }

  private updateTitleInfo(selectedData: ILdLogger | null) {
    const popupId = this.get().popupId;

    if (!popupId) {
      return;
    }

    this.floatingPopupService.updateTitleInfo(popupId, this.getTitleInfo(selectedData));
  }

  private getTitleInfo(selectedData: ILdLogger | null): ITranslationSubscription | undefined {
    if (!selectedData) {
      return undefined;
    }

    const loggerSerialNoMapToMeasurements = getLoggerSerialNoMapToMeasurements(
      this.allLoggerDeployments(),
      this.allLoggerMeasurements()
    );

    const isExistAnotherRelatedMeasurements =
      (loggerSerialNoMapToMeasurements[selectedData.deployment.serialNo]?.length ?? 0) > 1;

    return isExistAnotherRelatedMeasurements
      ? of(this.formattingData.formatTime(selectedData.measurement?.measStartTime, DayJSDateFormat.LT))
      : undefined;
  }
}
